import { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_BASE ?? "";

function withAuth(opts = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    credentials: "include",
    ...opts,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  };
}
async function fetchJsonStrict(url, opts) {
  const res = await fetch(url, withAuth(opts));
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status} on ${url}${text ? ` — ${text}` : ""}`);
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : null;
}
async function fetchJsonSoft(url, opts) {
  try { return await fetchJsonStrict(url, opts); } catch { return null; }
}

/* ----------------------- styled helpers ----------------------- */
const BTN     = "px-3 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20 hover:opacity-95";
const BTN_LITE= "px-3 py-2 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 text-white";
const BTN_OK  = "px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs";
const BTN_BAD = "px-2.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs";
const BTN_WARN= "px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-black text-xs";
const PILL_ON = "px-2 py-0.5 rounded-md bg-emerald-500 text-black text-xs";
const PILL_OFF= "px-2 py-0.5 rounded-md bg-white/20 text-white text-xs";

export default function UserManager() {
  const [cinemas, setCinemas] = useState([]); // [{cinema_id, cinema_name}]
  const [users, setUsers] = useState([]);
  const [filterCinema, setFilterCinema] = useState("");
  const [search, setSearch] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cinemaId, setCinemaId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [canStorno, setCanStorno] = useState(false);

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadCinemas = async () => {
    const data = await fetchJsonSoft(`${API}/api/cinemas`);
    const arr = Array.isArray(data) ? data : (data && Array.isArray(data.rows) ? data.rows : []);
    setCinemas(arr.map(r => ({
      cinema_id: r.cinema_id ?? r.CINEMA_ID,
      cinema_name: r.cinema_name ?? r.CINEMA_NAME ?? r.name ?? r.company_name ?? "Cinema",
    })));
  };

  const loadUsers = async () => {
    const url = filterCinema ? `${API}/api/users?cinema_id=${filterCinema}` : `${API}/api/users`;
    const data = await fetchJsonSoft(url);
    setUsers(Array.isArray(data) ? data : []);
  };

  useEffect(() => { loadCinemas(); }, []);
  useEffect(() => { loadUsers(); }, [filterCinema]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (!username || !password || !cinemaId) {
      setErr("Username, password and cinema are required.");
      return;
    }
    try {
      const res = await fetchJsonStrict(`${API}/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password,          // server will MD5 it
          cinema_id: Number(cinemaId),
          is_admin: isAdmin ? 1 : 0,
          can_storno: canStorno ? 1 : 0,
        }),
      });
      if (!res?.success) throw new Error(res?.error || "Create failed");
      setMsg("✅ User created");
      setUsername(""); setPassword(""); setCinemaId(""); setIsAdmin(false); setCanStorno(false);
      loadUsers();
    } catch (e) {
      setErr(e.message || "Create failed");
    }
  };

  const resetPassword = async (user_id) => {
    const pwd = prompt("New password for this user:");
    if (pwd == null) return;
    try {
      const res = await fetchJsonStrict(`${API}/api/users/${user_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pwd }),
      });
      if (!res?.success) throw new Error(res?.error || "Reset failed");
      setMsg("✅ Password updated");
    } catch (e) {
      setErr(e.message || "Reset failed");
    }
  };

  const toggleAdmin = async (u) => {
    try {
      const res = await fetchJsonStrict(`${API}/api/users/${u.user_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_admin: u.is_admin ? 0 : 1 }),
      });
      if (!res?.success) throw new Error(res?.error || "Update failed");
      setUsers((prev) => prev.map(x => x.user_id === u.user_id ? { ...x, is_admin: u.is_admin ? 0 : 1 } : x));
    } catch (e) {
      setErr(e.message || "Update failed");
    }
  };

  const toggleStorno = async (u) => {
    try {
      const res = await fetchJsonStrict(`${API}/api/users/${u.user_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ can_storno: u.can_storno ? 0 : 1 }),
      });
      if (!res?.success) throw new Error(res?.error || "Update failed");
      setUsers((prev) => prev.map(x => x.user_id === u.user_id ? { ...x, can_storno: u.can_storno ? 0 : 1 } : x));
    } catch (e) {
      setErr(e.message || "Update failed");
    }
  };

  const deleteUser = async (user_id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      const res = await fetchJsonStrict(`${API}/api/users/${user_id}`, { method: "DELETE" });
      if (!res?.success) throw new Error(res?.error || "Delete failed");
      setUsers(prev => prev.filter(x => x.user_id !== user_id));
      setMsg("✅ User deleted");
    } catch (e) {
      setErr(e.message || "Delete failed");
    }
  };

  const usersShown = useMemo(() => {
    const base = filterCinema ? users.filter(u => Number(u.cinema_id) === Number(filterCinema)) : users;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(u =>
      String(u.username||"").toLowerCase().includes(q) ||
      String(u.cinema_name||u.cinema_id||"").toLowerCase().includes(q)
    );
  }, [users, filterCinema, search]);

  return (
    <div className="min-h-screen relative p-4 md:p-6">
      {/* blurred background + purple gradient */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="bg"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* glass container */}
      <div className="max-w-6xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6 text-white">
        {/* top bar */}
        <div className="sticky top-4 z-10 -mt-2 mb-4 bg-transparent flex flex-wrap gap-2 items-center">
          <button className={BTN_LITE} onClick={() => window.history.back()}>← Back</button>

          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight ml-1">👤 User Manager</h1>

          <div className="ml-auto flex items-center gap-2">
            <select
              className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl"
              value={filterCinema}
              onChange={e => setFilterCinema(e.target.value)}
            >
              <option value="">🎬 All Cinemas</option>
              {cinemas.map(c => (
                <option key={c.cinema_id} value={c.cinema_id}>{c.cinema_name}</option>
              ))}
            </select>
            <input
              className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl w-56"
              placeholder="Search username / cinema…"
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
            />
            <button onClick={loadUsers} className={BTN_LITE}>↻ Refresh</button>
          </div>
        </div>

        {(msg || err) && (
          <div className="mb-4 flex gap-3">
            {msg && <span className="px-3 py-1 rounded-lg bg-emerald-500 text-black text-sm">{msg}</span>}
            {err && <span className="px-3 py-1 rounded-lg bg-rose-600 text-white text-sm">{err}</span>}
          </div>
        )}

        {/* Create form */}
        <form onSubmit={handleCreate} className="grid md:grid-cols-6 grid-cols-1 gap-2 mb-6 bg-white/10 border border-white/10 p-4 rounded-2xl">
          <input
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl md:col-span-2"
            placeholder="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
          />
          <input
            type="password"
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl md:col-span-2"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <select
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl"
            value={cinemaId}
            onChange={e => setCinemaId(e.target.value)}
          >
            <option value="">Select cinema…</option>
            {cinemas.map(c => (
              <option key={c.cinema_id} value={c.cinema_id}>{c.cinema_name}</option>
            ))}
          </select>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} /> Admin
            </label>
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={canStorno} onChange={e => setCanStorno(e.target.checked)} /> Can storno
            </label>
            <button className={`${BTN} ml-auto`}>➕ Create</button>
          </div>
        </form>

        {/* Users table */}
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white/10">
              <tr className="text-left">
                <Th>ID</Th>
                <Th>Username</Th>
                <Th>Cinema</Th>
                <Th>Admin</Th>
                <Th>Can storno</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {usersShown.map(u => (
                <tr key={u.user_id} className="border-t border-white/10 even:bg-white/5">
                  <Td>{u.user_id}</Td>
                  <Td>{u.username}</Td>
                  <Td>{u.cinema_name || u.cinema_id}</Td>
                  <Td>
                    <button
                      className={u.is_admin ? PILL_ON : PILL_OFF}
                      onClick={() => toggleAdmin(u)}
                      title="Toggle admin"
                    >
                      {u.is_admin ? "Yes" : "No"}
                    </button>
                  </Td>
                  <Td>
                    <button
                      className={u.can_storno ? PILL_ON : PILL_OFF}
                      onClick={() => toggleStorno(u)}
                      title="Toggle storno right"
                    >
                      {u.can_storno ? "Yes" : "No"}
                    </button>
                  </Td>
                  <Td className="py-3">
                    <div className="flex gap-2">
                      <button className={BTN_WARN} onClick={() => resetPassword(u.user_id)}>Reset password</button>
                      <button className={BTN_BAD} onClick={() => deleteUser(u.user_id)}>Delete</button>
                    </div>
                  </Td>
                </tr>
              ))}
              {!usersShown.length && (
                <tr><td className="p-4 text-white/80" colSpan={6}>No users…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* tiny table helpers */
const Th = ({ children }) => <th className="p-2 border border-white/10 font-semibold text-white/90">{children}</th>;
const Td = ({ children, className }) => <td className={`p-2 border border-white/10 ${className||""}`}>{children}</td>;
